import { Fragment } from "react";
import CodeBlock from "@/components/CodeBlock";

/**
 * Lightweight renderer for assistant text containing fenced code blocks.
 *
 * Splits the input on ```lang\n...\n``` fences. Plain text segments are
 * rendered preserving newlines (whitespace-pre-wrap upstream). Code blocks
 * render via CodeBlock with syntax highlighting + copy button.
 *
 * We deliberately do NOT parse other markdown (bold / italic / headings) —
 * the bubble already renders text verbatim so adding a full markdown parser
 * would change every other message in subtle ways. Code blocks are the only
 * thing users complained about looking like a wall of plain text.
 *
 * Streaming-safe: an unterminated final ``` (the model is mid-emit) is
 * rendered as an in-progress block so users see colors appear live instead
 * of waiting for the closing fence.
 *
 * Spec-correct: fences MUST be at the start of a line (per CommonMark) — we
 * enforce this so inline backticks inside markdown tables (e.g.
 *   | ```apex inline=true ```apex public… | …)
 * don't trick the parser into eating half the message as one giant block.
 */
const FENCE_RE = /(^|\n)```([\w+\-.#]*)[ \t]*\n([\s\S]*?)(?:\n```|$)/g;

export default function RichText({ children }) {
  const text = typeof children === "string" ? children : String(children || "");
  if (!text.includes("```")) {
    return <>{text}</>;
  }

  const parts = [];
  let lastIdx = 0;
  let match;
  let key = 0;
  while ((match = FENCE_RE.exec(text)) !== null) {
    const [full, leading, lang, code] = match;
    // Anchor the captured slice to the actual ``` (skip the leading \n we
    // included only to enforce line-start).
    const fenceStart = match.index + (leading ? leading.length : 0);
    if (fenceStart > lastIdx) {
      parts.push(
        <Fragment key={`t-${key++}`}>{text.slice(lastIdx, fenceStart)}</Fragment>
      );
    }
    parts.push(
      <CodeBlock key={`c-${key++}`} language={lang} value={code || ""} />
    );
    lastIdx = match.index + full.length;
  }
  if (lastIdx < text.length) {
    parts.push(<Fragment key={`t-${key++}`}>{text.slice(lastIdx)}</Fragment>);
  }
  return <>{parts}</>;
}
